/**
 * SOLOMON — CAPABILITY REGISTRY
 *
 * The declarative list of everything Solomon can do. One entry per capability.
 *
 * ZERO IMPORTS, deliberately and load-bearingly: this file is imported by
 * `node --test` with no bundler, AND by the backend parity gate across the
 * package boundary. Adding any import breaks both.
 *
 * `example` doubles as the entry's description and its prefill text — reading
 * the map teaches the invocation, so the map makes itself unnecessary.
 * Examples are known-good STARTING POINTS, not contracts: whether a phrasing
 * fires its tool runs through the lane classifier and the model's own
 * decision, which is probabilistic.
 *
 * MCP tools are NOT here. They are runtime-discovered, per-user and
 * namespaced; they render from the status payload instead.
 */
export const CAPABILITIES = [
  // ── Ask Solomon (native chat tools) ──────────────────────────────
  { id: 'query_leads', group: 'ask', label: 'Query leads', requires: null,
    example: 'show me leads in Hamilton County with high equity',
    invoke: { type: 'prefill' } },
  { id: 'query_comps', group: 'ask', label: 'Pull comps', requires: null,
    example: 'run comps on 412 W Main St, Indianapolis',
    invoke: { type: 'prefill' } },
  { id: 'identify_investors', group: 'ask', label: 'Identify investors', requires: null,
    example: "who's buying cash in 46208?",
    invoke: { type: 'prefill' } },
  { id: 'query_research_findings', group: 'ask', label: 'Search my research', requires: null,
    example: 'what did my research turn up on Indianapolis rental ordinances?',
    invoke: { type: 'prefill' } },
  { id: 'create_research_task', group: 'ask', label: 'Start a research task', requires: 'tavily',
    example: 'research Indianapolis rental ordinance changes weekly',
    invoke: { type: 'prefill' } },
  { id: 'schedule_automation', group: 'ask', label: 'Schedule an automation', requires: null,
    example: 'every weekday at 7am, digest my new leads',
    invoke: { type: 'prefill' } },
  { id: 'query_automations', group: 'ask', label: 'Query automations', requires: null,
    example: "what's my Monday digest's last run status?",
    invoke: { type: 'prefill' } },
  { id: 'generate_file', group: 'ask', label: 'Generate a file', requires: null,
    example: 'build me a seller net sheet as a spreadsheet',
    invoke: { type: 'prefill' } },
  { id: 'generate_image', group: 'ask', label: 'Generate images', requires: 'fal',
    example: 'generate 4 brand-mark concepts for The Veil World',
    invoke: { type: 'prefill' } },
  { id: 'web_search', group: 'ask', label: 'Search the web', requires: 'tavily',
    example: 'search the web for Marion County tax sale dates',
    invoke: { type: 'prefill' } },
  { id: 'onboard_mcp_server', group: 'ask', label: 'Onboard an MCP server', requires: null,
    example: 'onboard this MCP server: https://example.com/mcp',
    invoke: { type: 'prefill' } },
  { id: 'find_mcp_tools', group: 'ask', label: 'Find a connected tool', requires: null,
    example: 'find a tool that can pull option chains',
    invoke: { type: 'prefill' } },

  // ── Workspaces ───────────────────────────────────────────────────
  { id: 'wholesale', group: 'workspace', label: 'Wholesale', requires: null,
    example: 'Leads, call queue, dispositions, comps and appointments',
    invoke: { type: 'workspace', target: 'wholesale' } },
  { id: 'automations', group: 'workspace', label: 'Automations', requires: null,
    example: 'Scheduled jobs and their run history',
    invoke: { type: 'workspace', target: 'automations' } },
  { id: 'research', group: 'workspace', label: 'Research', requires: null,
    example: 'Standing research tasks and their findings',
    invoke: { type: 'workspace', target: 'research' } },

  // ── Tools ────────────────────────────────────────────────────────
  { id: 'signals', group: 'tool', label: 'Signals', requires: null,
    example: 'Routing, confidence and interaction analytics',
    invoke: { type: 'tool', target: 'signals' } },
  { id: 'learn', group: 'tool', label: 'Teach Solomon', requires: null,
    example: 'Feed it a URL, a document, audio or raw text',
    invoke: { type: 'tool', target: 'learn' } },
  { id: 'memory', group: 'tool', label: "Solomon's memory", requires: null,
    example: 'Browse what Solomon has learned and retained',
    invoke: { type: 'tool', target: 'memory' } },
  { id: 'discord_alerts', group: 'tool', label: 'Discord alerts', requires: 'discord',
    example: 'Dead letters, auto-pauses, run results and the morning briefing pushed to your Discord channel; message Solomon back with /sol',
    invoke: { type: 'workspace', target: 'automations', tab: 'automations' } },
  // No Google write-tools. D9 (Option B) rips Compose email, Calendar event,
  // Append sheet row, Create task and My tasks — they were unused WHILE
  // CONNECTED (5 events, 1 email, 2 drafts, 2 tasks, all April 2026), so
  // listing them dormant would have preserved residue, not capability.
  // `requires: 'google'` consequently has no consumers and is not a valid value.
];
